import { MCAT_SCORE_SECTION_MAX, MCAT_SCORE_SECTION_MIN, MCAT_SECTION_KEYS } from 'my-core/mcat/mcat-sections';

export type Country = 'CA' | 'US';
export type ProgramType = 'DO' | 'MD';
export type ProgramGroupKey = 'ca' | 'do' | 'md';
export type SectionKey = (typeof MCAT_SECTION_KEYS)[number];

export const SECTION_KEYS: readonly SectionKey[] = MCAT_SECTION_KEYS;

export const SECTION_MIN = MCAT_SCORE_SECTION_MIN;
export const SECTION_MAX = MCAT_SCORE_SECTION_MAX;
export const SCORE_MIN = SECTION_MIN * SECTION_KEYS.length;
export const SCORE_MAX = SECTION_MAX * SECTION_KEYS.length;

export type SectionScores = Readonly<Record<SectionKey, number>>;
export type PartialSectionScores = Readonly<Partial<Record<SectionKey, number>>>;
export type SectionMinimums = Readonly<Record<SectionKey, number | null>>;
export type SchoolFieldSource = Readonly<{ period: string; url: string }>;

export type School = Readonly<{
  acceptance_rate: number | null;
  as_of: string;
  cars_minimum: number | null;
  city: string;
  class_size: number | null;
  country: Country;
  field_sources: Readonly<Record<string, SchoolFieldSource>>;
  gpa_stat: string | null;
  in_region_share: number | null;
  key: string;
  mcat_minimum: number | null;
  mcat_stat: string | null;
  mcat_usage: string;
  median_gpa: number | null;
  median_mcat: number | null;
  name: string;
  region: string;
  region_name: string;
  residency_note: string | null;
  requirements_note: string | null;
  requirements_source_url: string | null;
  section_minimums: SectionMinimums | null;
  source_url: string | null;
  type: ProgramType;
  url: string | null;
}>;

export type PercentileSource = Readonly<{
  basis: string;
  effective: string;
  mean_total: number;
  name: string;
  url: string;
}>;

export type PercentileData = Readonly<{
  sections: Readonly<Record<SectionKey, Readonly<Record<string, number>>>>;
  source: PercentileSource;
  total: Readonly<Record<string, number>>;
}>;

export type Band = Readonly<{
  id: number;
  label: string;
  max: number;
  min: number;
  narrative: string;
  retake: string;
  sections_body?: string;
  sections_title?: string;
  verdict: string;
}>;

export type Faq = Readonly<{ a: string; q: string }>;

export type TitledBody = Readonly<{ body: string; title: string }>;

export type CtaCopy = Readonly<{ body: string; headline: string; label: string }>;

export type LabelledBody = Readonly<{ body: string; label: string }>;

export type SectionCopy = Readonly<{
  below_cutoff: string;
  name: string;
  risk: string;
  short: string;
  strength: string;
  watch: string;
}>;

export type CalculatorCopy = Readonly<{
  demoted_note: string;
  disclaimer: string;
  faqs: readonly Faq[];
  flags: Readonly<{ cars_minimum: string; mcat_minimum: string; section_minimum: string }>;
  form: Readonly<{
    country_label: string;
    gpa_error: string;
    gpa_label: string;
    mcat_error: string;
    mcat_label: string;
    program_label: string;
    region_label: string;
    sections_help: string;
    sections_label: string;
  }>;
  groups: Readonly<Record<'likely' | 'reach' | 'target', LabelledBody>>;
  h1: string;
  how_it_works: readonly TitledBody[];
  how_it_works_title: string;
  internal_links: Readonly<{ links: readonly Readonly<{ label: string; path: string }>[]; title: string }>;
  intro: string;
  intro_limitation: string;
  lizzym: TitledBody;
  methodology: TitledBody;
  results: Readonly<{
    all_title: string;
    empty: string;
    title: string;
    unrated_body: string;
    unrated_title: string;
  }>;
  title: string;
}>;

export type LeadCopy = Readonly<{
  add: string;
  bar_count: string;
  bar_dismiss: string;
  bar_label: string;
  body_empty: string;
  body_picked: string;
  clear: string;
  empty_list: string;
  label: string;
  label_empty: string;
  list_title: string;
  median_summary: string;
  no_median: string;
  picker_empty: string;
  picker_full: string;
  picker_label: string;
  picker_placeholder: string;
  promise: string;
  remove: string;
  title_empty: string;
  title_picked: string;
  title_picked_one: string;
}>;

export type ChartsCopy = Readonly<{
  axis_label: string;
  choose_score: string;
  distribution_desc: string;
  distribution_hint: string;
  distribution_note: string;
  distribution_title: string;
  field_desc: string;
  field_bands_note: string;
  field_coverage: string;
  field_desc_empty: string;
  field_hint: string;
  field_title: string;
  histogram_desc: string;
  histogram_title: string;
  strip_above: string;
  strip_caption: string;
  strip_count: string;
  strip_desc: string;
  strip_meets: string;
  strip_title: string;
  tracks_desc: string;
  tracks_empty: string;
  tracks_minimums: string;
  tracks_scale: string;
  tracks_title: string;
  you: string;
  your_score: string;
}>;

export type ScorePageCopy = Readonly<{
  example_split_body: string;
  example_split_title: string;
  faqs: readonly Faq[];
  gpa_body: string;
  gpa_label: string;
  gpa_title: string;
  group_labels: Readonly<Record<'at_or_above' | 'below' | 'near', string>>;
  h1: string;
  hero_cta: string;
  hero_gpa_link: string;
  hub_link: string;
  lead_fallback: string;
  lead_none: string;
  nearby_body: string;
  nearby_title: string;
  next_label: string;
  not_found_body: string;
  not_found_link: string;
  not_found_title: string;
  percentile_caption: string;
  percentile_title: string;
  prev_label: string;
  program_tabs: Readonly<Record<'ca' | 'do' | 'md', string>>;
  retake_title: string;
  retake_prep_link: string;
  retake_calculator_link: string;
  schools_body: string;
  schools_title: string;
  sections_body: string;
  sections_sum_error: string;
  sections_title: string;
  show_fewer: string;
  show_more: string;
  sum_error_link: string;
  task_nav: Readonly<{ label: string; retake: string; schools: string; sections: string }>;
  title: string;
}>;

export type HubCopy = Readonly<{
  meta_description: string;
  band_link: string;
  band_title: string;
  calculator_link: string;
  faqs: readonly Faq[];
  h1: string;
  intro: string;
  percentile_note: string;
  percentile_title: string;
  picker_button: string;
  picker_label: string;
  sections_note: string;
  sections_title: string;
  standing: string;
  title: string;
}>;

export type SectionFeedbackCopy = Readonly<{
  balance: Readonly<Record<'balanced' | 'lopsided' | 'mostly_balanced', Readonly<{ label: string }>>>;
  cars_canada: string;
  cutoff_list_title: string;
  lowest_lead: Readonly<{ level: string; one: string; other: string; tied_one: string; tied_other: string }>;
  neutral: string;
  retake: Readonly<Record<'consider_section' | 'no' | 'no_but_watch' | 'yes_section' | 'yes_total', string>>;
  sections: Readonly<Record<SectionKey, SectionCopy>>;
}>;

export type MedSchoolCopy = Readonly<{
  bands: readonly Band[];
  calculator: CalculatorCopy;
  charts: ChartsCopy;
  data_note: string;
  fit_check: Readonly<{ promise: string; sections_balanced: CtaCopy; sections_uneven: CtaCopy }>;
  hub: HubCopy;
  lead: LeadCopy;
  prep_cta: CtaCopy;
  score_page: ScorePageCopy;
  score_pages?: Readonly<Record<string, Readonly<{ lead?: string }>>>;
  section_feedback: SectionFeedbackCopy;
}>;
